import { FormInstance } from 'antd/lib/form';
import { set } from 'lodash';
import { useCallback } from 'react';

type Options = {
  /**
   * Unique key used for removing items.
   * @default 'id'
   */
  idKey?: string;
  addAsFirst?: boolean;
};

export function useFormList<Item = any>(
  form: FormInstance,
  fieldName: string | (string | number)[],
  options: Options
) {
  const { idKey = 'id', addAsFirst = false } = options;

  const add = useCallback(
    (newItem: Item) => {
      const value: Item[] = form.getFieldValue(fieldName) ?? [];
      const newValue = addAsFirst ? [newItem, ...value] : [...value, newItem];

      if (typeof fieldName === 'string') {
        form.setFieldsValue({
          [fieldName]: newValue,
        });
      } else {
        const [key, ...rest] = fieldName;
        const nestedValue = form.getFieldValue(key);
        form.setFieldsValue({
          [key]: set(nestedValue, rest, newValue),
        });
      }
    },
    [form, fieldName, addAsFirst]
  );

  const remove = useCallback(
    (id: string | number) => {
      const value: Item[] = form.getFieldValue(fieldName);
      const newValue = value.filter(
        ({ [idKey]: currentItemId }: any) => currentItemId !== id
      );
      if (typeof fieldName === 'string') {
        form.setFieldsValue({
          [fieldName]: newValue,
        });
      } else {
        const [key, ...rest] = fieldName;
        const nestedValue = form.getFieldValue(key);
        form.setFieldsValue({
          [key]: set(nestedValue, rest, newValue),
        });
      }
    },
    [form, fieldName, idKey]
  );

  return {
    add,
    remove,
  };
}
